import React, { useState, useEffect } from 'react';
import { Gallery } from '../../types/Gallery';
import { BlogPost } from '../../types/Blog';
import HelpButton from '../Admin/HelpButton';

interface SitemapGeneratorProps {
  galleries: Gallery[];
  blogPosts: BlogPost[];
  baseUrl: string;
}

interface SitemapUrl {
  loc: string;
  lastmod: string;
  changefreq: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
  priority: number;
}

const SitemapGenerator: React.FC<SitemapGeneratorProps> = ({ galleries, blogPosts, baseUrl }) => {
  const [sitemapUrls, setSitemapUrls] = useState<SitemapUrl[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);

  const generateSitemap = () => {
    setIsGenerating(true);
    
    const urls: SitemapUrl[] = [
      // Main pages
      {
        loc: `${baseUrl}/`,
        lastmod: new Date().toISOString().split('T')[0],
        changefreq: 'weekly',
        priority: 1.0
      },
      {
        loc: `${baseUrl}/galleries`,
        lastmod: new Date().toISOString().split('T')[0],
        changefreq: 'weekly',
        priority: 0.9
      },
      {
        loc: `${baseUrl}/blog`,
        lastmod: new Date().toISOString().split('T')[0],
        changefreq: 'weekly',
        priority: 0.8
      },
      {
        loc: `${baseUrl}/about`,
        lastmod: new Date().toISOString().split('T')[0],
        changefreq: 'monthly',
        priority: 0.7
      },
      {
        loc: `${baseUrl}/contact`,
        lastmod: new Date().toISOString().split('T')[0],
        changefreq: 'monthly',
        priority: 0.7
      }
    ];

    // Add gallery pages
    galleries.forEach(gallery => {
      const gallerySlug = gallery.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      urls.push({
        loc: `${baseUrl}/galleries/${gallerySlug}`,
        lastmod: new Date(gallery.updatedAt || gallery.createdAt).toISOString().split('T')[0],
        changefreq: 'monthly',
        priority: 0.8
      });
    });

    // Add blog post pages
    blogPosts.forEach(post => {
      urls.push({
        loc: `${baseUrl}/blog/${post.slug}`,
        lastmod: new Date(post.updatedAt || post.createdAt).toISOString().split('T')[0],
        changefreq: 'monthly',
        priority: 0.6
      });
    });

    setSitemapUrls(urls);
    setIsGenerating(false);
  };

  const generateXMLSitemap = () => {
    const sitemap = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${sitemapUrls.map(url => `  <url>
    <loc>${url.loc}</loc>
    <lastmod>${url.lastmod}</lastmod>
    <changefreq>${url.changefreq}</changefreq>
    <priority>${url.priority}</priority>
  </url>`).join('\n')}
</urlset>`;

    return sitemap;
  };

  const downloadSitemap = () => {
    const sitemap = generateXMLSitemap();
    const blob = new Blob([sitemap], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sitemap.xml';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const copySitemapToClipboard = () => {
    const sitemap = generateXMLSitemap();
    navigator.clipboard.writeText(sitemap).then(() => {
      alert('Sitemap copied to clipboard!');
    });
  };

  useEffect(() => {
    generateSitemap();
  }, [galleries, blogPosts, baseUrl]);

  return (
    <div className="sitemap-generator">
      <div className="sitemap-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <h3>XML Sitemap Generator</h3>
          <HelpButton section="seo-sitemap" tooltip="Sitemap Generator Help" />
        </div>
        <p>Generate and download your website's sitemap for better SEO</p>
      </div>

      <div className="sitemap-stats">
        <div className="stat-item">
          <span className="stat-number">{sitemapUrls.length}</span>
          <span className="stat-label">Total URLs</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{galleries.length}</span>
          <span className="stat-label">Gallery Pages</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{blogPosts.length}</span>
          <span className="stat-label">Blog Posts</span>
        </div>
      </div>

      <div className="sitemap-actions">
        <button 
          className="btn btn-primary"
          onClick={generateSitemap}
          disabled={isGenerating}
        >
          {isGenerating ? 'Generating...' : 'Regenerate Sitemap'}
        </button>
        <button 
          className="btn btn-secondary"
          onClick={downloadSitemap}
          disabled={sitemapUrls.length === 0}
        >
          <i className="fas fa-download"></i> Download XML
        </button>
        <button 
          className="btn btn-outline"
          onClick={copySitemapToClipboard}
          disabled={sitemapUrls.length === 0}
        >
          <i className="fas fa-copy"></i> Copy to Clipboard
        </button>
      </div>

      <div className="sitemap-preview">
        <h4>Sitemap Preview</h4>
        <div className="url-list">
          {sitemapUrls.slice(0, 10).map((url, index) => (
            <div key={index} className="url-item">
              <span className="url-loc">{url.loc}</span>
              <span className="url-priority">Priority: {url.priority}</span>
            </div>
          ))}
          {sitemapUrls.length > 10 && (
            <div className="url-item more">
              ... and {sitemapUrls.length - 10} more URLs
            </div>
          )}
        </div>
      </div>

      <div className="sitemap-instructions">
        <h4>How to Use Your Sitemap:</h4>
        <ol>
          <li>Download the sitemap.xml file</li>
          <li>Upload it to your website's root directory</li>
          <li>Submit it to Google Search Console</li>
          <li>Add the sitemap URL to your robots.txt file</li>
        </ol>
      </div>
    </div>
  );
};

export default SitemapGenerator;






































